import { useState } from 'react';
import { submitForm, buildMailtoFallback } from './submit-form.js';

// ─────────────────────────────────────────────────────────────────────
// RotterdamForm — premium offerte form (revised spec).
//
//   • Compact success message on the same card (no confetti, no countdown)
//   • Postcode is now REQUIRED
//   • Numbered field indicators
//   • Gradient border on the submit button (visible on hover)
//   • "Reactie zo snel mogelijk" instead of a hard time promise
// ─────────────────────────────────────────────────────────────────────
export function RotterdamForm({ variant = 'hero', cityName }) {
  const [form, setForm] = useState({ naam: '', telefoon: '', email: '', postcode: '', woonplaats: '', ongedierte: '', omschrijving: '' });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [savedName, setSavedName] = useState('');

  // ── validation ────────────────────────────────────────────
  const validators = {
    naam: (v) => v.trim().length >= 2,
    telefoon: (v) => /^[0-9\s\-+()]{10,}$/.test(v.replace(/\s/g, '')),
    email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
    postcode: (v) => /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/.test(v.trim()),
    woonplaats: (v) => v.trim().length >= 2,
    ongedierte: (v) => v !== '',
  };
  const errors = {
    naam: 'Vul ten minste 2 tekens in',
    telefoon: 'Vul een geldig telefoonnummer in',
    email: 'Vul een geldig e-mailadres in',
    postcode: 'Bijv. 3011 AB',
    woonplaats: 'Vul uw woonplaats in',
    ongedierte: 'Maak een keuze',
  };
  const isValid = (k) => validators[k](form[k]);
  // showErr: laat error zien zodra veld 'touched' is en niet valid.
  // Was eerder ook 'form[k] !== ""' wat lege velden onzichtbaar maakte → user
  // klikt submit met leeg veld, geen feedback. Bug op mobile waar autocomplete
  // soms velden leeg laat. Fix: rode error wel tonen voor lege required velden.
  const showErr = (k) => touched[k] && !isValid(k);
  const showOk  = (k) => isValid(k) && form[k] !== '';

  const upd = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setTouched((t) => ({ ...t, [k]: true }));
  };
  const onBlur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const [error, setError] = useState('');
  const [mailtoFallback, setMailtoFallback] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const required = ['naam', 'telefoon', 'email', 'postcode', 'woonplaats', 'ongedierte'];
    setTouched(required.reduce((acc, k) => ({ ...acc, [k]: true }), {}));
    const allOk = required.every((k) => validators[k](form[k]));
    if (!allOk) {
      // Toon centrale feedback zodat user niet denkt dat de knop kapot is.
      setError('Vul alle verplichte velden correct in. Velden met een fout zijn rood gemarkeerd.');
      // Scroll naar het eerste ongeldige veld zodat user direct ziet wat mist.
      const firstInvalid = required.find((k) => !validators[k](form[k]));
      if (firstInvalid && typeof document !== 'undefined') {
        const el = document.querySelector(`[name="${firstInvalid}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // setTimeout zodat scroll eerst gebeurt voor focus (anders snapt iOS niet)
          setTimeout(() => el.focus({ preventScroll: true }), 350);
        }
      }
      return;
    }

    setSubmitting(true);
    setError('');
    const naamInput = (form.naam || '').trim();
    const firstWord = naamInput.split(/\s+/).filter(Boolean)[0] || '';
    setSavedName(firstWord.length > 1 ? firstWord : '');

    const formNaam = `Contactformulier ${form.ongedierte || 'Algemeen'}`;
    const fieldsPayload = {
      Naam: form.naam,
      Telefoon: form.telefoon,
      Email: form.email,
      Postcode: form.postcode,
      Woonplaats: form.woonplaats,
      Ongedierte: form.ongedierte,
      Omschrijving: form.omschrijving || '(geen omschrijving gegeven)',
    };
    const { ok, error: errMsg, errorType } = await submitForm({
      subject: formNaam,
      formName: formNaam,
      fields: fieldsPayload,
    });

    setSubmitting(false);
    if (ok) {
      setDone(true);
    } else {
      // Categoriseer fout zodat user de juiste actie krijgt voorgesteld
      let msg = 'Helaas, het versturen lukte niet via de site.';
      if (errorType === 'network' || errorType === 'timeout') {
        msg = 'Geen internet-verbinding of server reageert niet. Probeer opnieuw of mail rechtstreeks.';
      } else if (errorType === 'api') {
        msg = 'Server gaf een fout terug. Mail rechtstreeks of bel ons.';
      }
      setError(`${msg} Bel ons op 085 500 50 50 of mail direct via de knop hieronder.`);
      // Bouw mailto-fallback zodat user 1-klik kan mailen vanuit hun eigen mail-app
      setMailtoFallback(buildMailtoFallback({ subject: formNaam, fields: fieldsPayload }));
      console.error('[RotterdamForm]', errorType, errMsg);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ naam: '', telefoon: '', email: '', postcode: '', woonplaats: '', ongedierte: '', omschrijving: '' });
    setTouched({});
  };

  return (
    <div className={`rdam-form-card rdam-form-card--${variant}`}>
      {done ? (
        <RotterdamFormSuccess name={savedName} />
      ) : (
        <>
          <div className="rfc-head">
            <span className="rfc-urgency">
              <span className="rfc-live-dot" aria-hidden="true"></span>
              Reactie zo snel mogelijk
            </span>
            <h3 className="rfc-title">Vraag gratis offerte aan</h3>
            <p className="rfc-sub">Vul kort in wat er speelt, wij bellen u terug.</p>
            <div className="rfc-divider" aria-hidden="true"></div>
          </div>

          <form onSubmit={submit} className="rfc-form" noValidate>
            <FloatingField
              n={1}
              name="naam"
              label="Uw naam"
              type="text"
              value={form.naam}
              onChange={upd('naam')}
              onBlur={onBlur('naam')}
              autoComplete="name"
              ok={showOk('naam')}
              err={showErr('naam')}
              errMsg={errors.naam}
            />

            <div className="rfc-row">
              <FloatingField
                n={2}
                name="telefoon"
                label="Telefoon"
                type="tel"
                value={form.telefoon}
                onChange={upd('telefoon')}
                onBlur={onBlur('telefoon')}
                autoComplete="tel"
                inputMode="tel"
                ok={showOk('telefoon')}
                err={showErr('telefoon')}
                errMsg={errors.telefoon}
              />
              <FloatingField
                n={3}
                name="email"
                label="E-mailadres"
                type="email"
                value={form.email}
                onChange={upd('email')}
                onBlur={onBlur('email')}
                autoComplete="email"
                inputMode="email"
                ok={showOk('email')}
                err={showErr('email')}
                errMsg={errors.email}
              />
            </div>

            <div className="rfc-row">
              <FloatingField
                n={4}
                name="postcode"
                label="Postcode"
                type="text"
                value={form.postcode}
                onChange={upd('postcode')}
                onBlur={onBlur('postcode')}
                autoComplete="postal-code"
                ok={showOk('postcode')}
                err={showErr('postcode')}
                errMsg={errors.postcode}
              />
              <FloatingField
                n={5}
                name="woonplaats"
                label="Woonplaats"
                type="text"
                value={form.woonplaats}
                onChange={upd('woonplaats')}
                onBlur={onBlur('woonplaats')}
                autoComplete="address-level2"
                ok={showOk('woonplaats')}
                err={showErr('woonplaats')}
                errMsg={errors.woonplaats}
              />
            </div>

            <FloatingSelect
              n={6}
              name="ongedierte"
              label="Welk ongedierte?"
              value={form.ongedierte}
              onChange={upd('ongedierte')}
              onBlur={onBlur('ongedierte')}
              ok={showOk('ongedierte')}
              err={showErr('ongedierte')}
              errMsg={errors.ongedierte}
            />

            <FloatingTextarea
              name="omschrijving"
              label="Omschrijving (optioneel)"
              value={form.omschrijving}
              onChange={upd('omschrijving')}
              hint="Bijv. muis op zolder, wespennest onder dakgoot, etc."
            />

            <div className="rfc-submit-wrap">
              <button
                type="submit"
                className={`rfc-submit${submitting ? ' is-loading' : ''}`}
                disabled={submitting}
              >
                <span className="rfc-submit-shine" aria-hidden="true"></span>
                <span className="rfc-submit-text">Vraag offerte aan →</span>
                <span className="rfc-submit-spinner" aria-hidden="true"></span>
              </button>
              {error && (
                <div role="alert" style={{ marginTop: 10, padding: '12px 14px', background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.3)', borderRadius: 8, color: '#B91C1C', fontSize: '.86rem' }}>
                  <div style={{ marginBottom: mailtoFallback ? 10 : 0 }}>{error}</div>
                  {mailtoFallback && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <a
                        href={mailtoFallback}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          gap: 8, padding: '10px 14px',
                          background: '#1B2A4A', color: 'white',
                          borderRadius: 8, textDecoration: 'none',
                          fontWeight: 700, fontSize: '.88rem',
                        }}
                      >
                        ✉️ Mail direct vanaf mijn mail-app
                      </a>
                      <a
                        href="tel:0855005050"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          gap: 8, padding: '10px 14px',
                          background: '#E85D04', color: 'white',
                          borderRadius: 8, textDecoration: 'none',
                          fontWeight: 700, fontSize: '.88rem',
                        }}
                      >
                        📞 Bel 085 500 50 50
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Handgeschreven trust-mark: personalisatie per stad als
                cityName is doorgegeven, anders generic "in de Randstad".
                Caveat-font (Google Fonts, geladen via index.html).
                Trust-strip (Gratis/Vrijblijvend/Gecertificeerde partners)
                verwijderd 2026-05-28 op verzoek user — handgeschreven mark
                is genoeg trust-signaal en visueel rustiger. */}
            <div className="rfc-handwritten">
              Al sinds 2017 vertrouwd in {cityName || 'de Randstad'}
            </div>
            {false && (
              <div className="rfc-trust-strip">
                <span className="rfc-trust-item">
                  <CheckIcon /> Gratis
                </span>
                <span className="rfc-trust-item">
                  <CheckIcon /> Vrijblijvend
                </span>
                <span className="rfc-trust-item">
                  <CheckIcon /> Gecertificeerde partners
                </span>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
}

// ── small inline check icon ──────────────────────────────────────────
export function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

// ── Floating-label text input with numbered indicator ────────────────
export function FloatingField({ n, name, label, value, onChange, onBlur, ok, err, errMsg, ...rest }) {
  const cls = [
    'rfc-field',
    value && 'has-value',
    ok && 'is-valid',
    err && 'is-error',
  ].filter(Boolean).join(' ');
  return (
    <label className={cls}>
      {typeof n === 'number' && <span className="rfc-field-num" aria-hidden="true">{n}</span>}
      <input
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder=" "
        {...rest}
      />
      <span className="rfc-field-label">{label}</span>
      {ok && <span className="rfc-field-check" aria-hidden="true">✓</span>}
      {err && <span className="rfc-field-msg">{errMsg}</span>}
    </label>
  );
}

// ── Floating-label select with numbered indicator ────────────────────
function FloatingSelect({ n, name, label, value, onChange, onBlur, ok, err, errMsg }) {
  const cls = [
    'rfc-field rfc-field--select',
    value && 'has-value',
    ok && 'is-valid',
    err && 'is-error',
  ].filter(Boolean).join(' ');
  return (
    <label className={cls}>
      {typeof n === 'number' && <span className="rfc-field-num" aria-hidden="true">{n}</span>}
      <select name={name} value={value} onChange={onChange} onBlur={onBlur}>
        <option value="" disabled hidden></option>
        <option>Muizen</option>
        <option>Ratten</option>
        <option>Wespen</option>
        <option>Bedwantsen</option>
        <option>Mieren</option>
        <option>Kakkerlakken</option>
        <option>Spinnen</option>
        <option>Rioolvliegjes</option>
        <option>Vliegen</option>
        <option>Vlooien</option>
        <option>Zilvervisjes</option>
        <option>Papiervisjes</option>
        <option>Anders / weet ik niet</option>
      </select>
      <span className="rfc-field-label">{label}</span>
      <span className="rfc-field-chevron" aria-hidden="true"></span>
      {ok && <span className="rfc-field-check" aria-hidden="true">✓</span>}
      {err && <span className="rfc-field-msg">{errMsg}</span>}
    </label>
  );
}

// ── Optionele textarea voor extra context bij offerte-aanvraag ───────
function FloatingTextarea({ name, label, value, onChange, hint }) {
  const cls = [
    'rfc-field rfc-field--textarea',
    value && 'has-value',
  ].filter(Boolean).join(' ');
  return (
    <label className={cls}>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder=" "
        rows={2}
      />
      <span className="rfc-field-label">{label}</span>
      {hint && !value && <span className="rfc-field-hint">{hint}</span>}
    </label>
  );
}

// ── Success — animated checkmark with burst rays, single promise row ──
function RotterdamFormSuccess({ name }) {
  return (
    <div className="sc-success">
      {/* Visual: pulsing burst rays + checkmark circle */}
      <div className="sc-visual">
        <div className="sc-burst" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="sc-spark" style={{ '--i': i }}></span>
          ))}
        </div>
        <div className="sc-circle">
          <svg viewBox="0 0 52 52" fill="none" aria-hidden="true">
            <circle className="sc-ring" cx="26" cy="26" r="24" stroke="#E85D04" strokeWidth="2.5"/>
            <path className="sc-check" d="M14 26l8 8 16-16" stroke="#E85D04"
                  strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      <div className="sc-text">
        <h3>Geregeld{name ? <>, {name}</> : null}!</h3>
        <p>Uw aanvraag is ontvangen. Wij nemen zo snel mogelijk contact met u op.</p>
      </div>

      <div className="sc-promise">
        <div className="sc-promise-icon" aria-hidden="true">📋</div>
        <div className="sc-promise-text">
          <strong>Wat gebeurt er nu?</strong>
          <span>Wij bellen u zo snel mogelijk · Ma, Zo 07:00-22:00</span>
        </div>
      </div>

      <div className="sc-actions">
        <a href="tel:0855005050" className="sc-btn-call">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.64 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.81-.81a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 17z"/>
          </svg>
          Bel ons nu, 085 500 50 50
        </a>
      </div>

      <div className="sc-badges">
        <span>🛡️ Gecertificeerde partners</span>
        <span>★ 4,6 Google</span>
        <span>✓ Gecertificeerd</span>
      </div>
    </div>
  );
}
